import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  annulerUneDemandeDeCours,
  creerUnCours,
  creerUneDemandeDeCours,
  lireLArrivee,
  repondreAUneDemandeDeCours,
  terminerUnCours,
} from '@groupe-trousseau/shared/api-client';
import { deballer } from '../../shared/api/client';
import { CLES } from '../../shared/donnees/cles';

/**
 * **L'écran d'arrivée — `CDC-4` F-48 pour un élève, F-49 pour un tuteur.**
 *
 * **Un hook nommé, et la page n'appelle jamais l'API.** `web/CLAUDE.md` : « une page
 * n'appelle jamais l'API : elle consomme un hook de données nommé ». C'est aussi ce qui
 * fait qu'aucun `fetch` n'existe dans ce dépôt hors de l'enrobage du client généré.
 *
 * **La réponse se discrimine par le rôle**, et les deux formes n'ont ni les mêmes blocs ni
 * le même nombre : trois d'un côté, quatre de l'autre. Le type généré porte l'union, donc
 * **le compilateur refuse une page d'élève qui lirait un bloc de tuteur**.
 *
 * ═══════════════════════════════════════════════════════════════════════════
 * **CETTE FEATURE N'AVAIT AUCUNE MUTATION JUSQU'AU 20260825, ET C'EST TOUT L'OBJET DE
 * D-097.**
 * ═══════════════════════════════════════════════════════════════════════════
 *
 * Elle n'exposait que la lecture ci-dessus — **la seule des six features de `web/` dans ce
 * cas**. Or l'écran d'arrivée porte **cinq gestes** que le cahier des charges lui donne
 * nommément, et ses cinq boutons étaient dessinés **sans `onClick`**.
 *
 * **LA CONSÉQUENCE ÉTAIT TOTALE, PAS COSMÉTIQUE.** `POST /api/v1/cours` n'avait **aucun
 * appelant dans aucun front**, et le jeu de recette ne sème aucun cours : **aucun cours
 * n'existait, et aucune surface ne pouvait en faire naître un.** Le tableau, la visio, le
 * chat d'un cours, la relecture, la mesure du temps et l'observation en direct — tout
 * `CDC-2` et une moitié de `CDC-4` — étaient hors d'atteinte depuis un navigateur. Vingt et
 * une routes livrées, éprouvées par des parcours automatisés, et **aucune atteignable par
 * une personne**.
 *
 * **AUCUN CONTRÔLE DE CE DÉPÔT NE POUVAIT LE VOIR.** Le typage accepte un `<Bouton>` sans
 * `onClick`, la propriété étant facultative ; le lint, la barrière de couches et le `build`
 * passent. **Et les parcours de bout en bout passent aussi** : ils appellent l'API **par son
 * URL**, donc ils prouvent qu'une route répond et **jamais qu'une surface la déclenche**.
 * C'est la structure de D-087 — « le test franchissait le passage par un chemin que la
 * production n'a pas ».
 *
 * **C'est D-068 pris par l'autre bout** : là-bas « la route existait, le client généré ne
 * pouvait pas l'appeler », et le compilateur du front avait fini par le dire. Ici **le
 * client peut l'appeler et personne ne l'appelle** — rien ne le dit, un appel absent n'ayant
 * pas de type.
 */
export function useArrivee() {
  return useQuery({
    queryKey: CLES.arrivee,
    queryFn: () => deballer(lireLArrivee()),
    // **Rien de cet écran n'est un objet conservé** (F-54) : « tout ce que le produit
    // affiche se calcule à l'instant ». Une présence de trente secondes vieillit vite,
    // donc la donnée se rafraîchit à chaque montage plutôt que de mentir.
    staleTime: 15_000,
  });
}

/**
 * ═══════════════════════════════════════════════════════════════════════════
 * **CE QUE CHAQUE GESTE INVALIDE, ET LA LISTE N'EST PAS LA MÊME POUR TOUS.**
 * ═══════════════════════════════════════════════════════════════════════════
 *
 * `DOCTRINE.md` §6 : « les clés de cache sont centralisées par feature dans une fabrique.
 * **Une clé improvisée casse l'invalidation en silence.** » Et la fabrique de ce dossier
 * est **commune aux features**, précisément « parce que l'invalidation traverse les
 * features » — c'est ce qui autorise celle-ci à invalider l'historique et l'annuaire sans
 * importer l'intérieur de personne.
 *
 * **Terminer un cours invalide l'HISTORIQUE, et c'est le cas qui se serait oublié** : un
 * cours terminé quitte les cours non terminés **et entre dans l'historique**, avec ses deux
 * durées et ses totaux. Sans cette invalidation, l'historique montrerait un cours de moins
 * jusqu'au prochain rechargement complet.
 *
 * **Répondre à une demande invalide l'ANNUAIRE et les CONVERSATIONS** : la décision « paraît
 * dans le fil » (F-14 critère 2), donc le dernier message de la ligne d'annuaire change.
 *
 * **CHAQUE GESTE POSE SA PROPRE LISTE, ET IL N'Y A PAS D'UTILITAIRE COMMUN.** Une première
 * écriture en avait un — `useGesteDeLArrivee`, qui invalidait l'arrivée. Il n'a fini par
 * servir **qu'à un seul des cinq gestes**, les quatre autres ayant chacun une liste plus
 * large : un utilitaire à un seul appelant est un fichier de plus qui ne factorise rien, et
 * surtout **il aurait rendu invisible que les listes diffèrent** — ce qui est précisément
 * l'information qu'on vient chercher en relisant ce bloc.
 */

/**
 * ═══════════════════════════════════════════════════════════════════════════
 * **CRÉER UN COURS — F-49, ET LE GESTE PART DE CET ÉCRAN.**
 * ═══════════════════════════════════════════════════════════════════════════
 *
 * « **La création d'un cours part de cet écran**, et le sélecteur de structure n'apparaît que
 * s'il a deux rattachements. »
 *
 * **`tuteurId` EST OMIS, ET C'EST CE QUE LE CONTRAT DEMANDE** : « à omettre quand l'appelant
 * est lui-même ce tuteur ». Le front ne dit donc jamais qui tient le cours — le serveur le
 * lit de la session, et **c'est ce qui rend impossible d'ouvrir un cours au nom de
 * quelqu'un d'autre depuis cette surface**.
 *
 * **`organizationId` n'est passé QUE sur deux rattachements**, même règle : « à omettre
 * quand le tuteur n'en a qu'un ». Le passer toujours obligerait la page à connaître une
 * structure là où la règle dit qu'elle n'a pas à la choisir.
 */
export function useCreerUnCours() {
  const cache = useQueryClient();
  return useMutation({
    mutationFn: (demande: {
      titre: string;
      description: string;
      elevesIds: string[];
      organizationId?: string;
    }) =>
      deballer(
        creerUnCours({
          body: {
            titre: demande.titre,
            description: demande.description,
            elevesIds: demande.elevesIds,
            ...(demande.organizationId === undefined
              ? {}
              : { organizationId: demande.organizationId }),
          },
        }),
      ),
    // **L'arrivée seule, et c'est le seul des cinq gestes dans ce cas** : un cours neuf
    // n'entre ni dans l'historique — il n'est pas terminé —, ni dans l'annuaire, ni dans
    // aucun fil. Il paraît sur cet écran, et nulle part ailleurs.
    onSuccess: () => {
      void cache.invalidateQueries({ queryKey: CLES.arrivee });
    },
  });
}

/**
 * **TERMINER UN COURS depuis l'écran d'arrivée — F-49, et c'est un SECOND chemin assumé.**
 *
 * Le premier est dans le tableau, `features/cours/tableau.ts`. **Deux chemins pour un geste
 * est ce que Q566 refuse en général**, et ici la règle l'exige : « au plafond de deux cours
 * non terminés, le geste de création est refusé avec sa raison, et **l'écran NOMME les cours
 * qu'il peut terminer** pour libérer une place — sinon le refus est un mur sans issue ».
 *
 * **Nommer un cours sans permettre de le terminer laisserait le mur**, et obligerait à
 * entrer dans le cours pour en sortir. Ce qui rend les deux chemins sûrs est qu'ils appellent
 * **la même route** : il n'y a **pas deux autorisations à tenir d'accord**, qui est le risque
 * exact que Q566 nomme — « celle qu'on oublie est la faille ».
 *
 * **Il invalide l'historique en plus de l'arrivée** : un cours terminé y entre.
 */
export function useTerminerUnCoursDepuisLArrivee() {
  const cache = useQueryClient();
  return useMutation({
    mutationFn: (coursId: string) =>
      deballer(terminerUnCours({ path: { id: coursId } })),
    onSuccess: () => {
      void cache.invalidateQueries({ queryKey: CLES.arrivee });
      void cache.invalidateQueries({ queryKey: CLES.historique.tout });
    },
  });
}

/**
 * ═══════════════════════════════════════════════════════════════════════════
 * **DEMANDER UN COURS — F-48, DEPUIS LA LIGNE D'UN TUTEUR.**
 * ═══════════════════════════════════════════════════════════════════════════
 *
 * « La demande de cours part de cet écran, depuis la ligne d'un tuteur, **y compris s'il est
 * occupé** — c'est son second usage. »
 *
 * **LA CONVERSATION EST OBLIGATOIRE, ET ELLE N'ÉTAIT PAS DANS LA RÉPONSE JUSQU'AU
 * 20260825.** F-14 : « la demande est rattachée à la conversation où elle vit, et le modèle
 * le porte. **C'est sur cette conversation que se lit le cloisonnement de ses deux champs de
 * texte** », et « sans ce rattachement la phrase n'est pas exécutable ». `mesTuteurs` la
 * porte désormais — voir D-097.
 *
 * **DEUX CHAMPS, ET LE SECOND EST FACULTATIF** (Q555) : ce que l'élève veut travailler,
 * **obligatoire** ; ce qu'il n'arrive pas à faire, **facultatif**. « Le premier obligatoire
 * et le second facultatif est le patron du cours. » **Aucune longueur maximum n'est une règle
 * du produit** — le front n'en impose donc aucune.
 *
 * **Il invalide aussi les CONVERSATIONS** : « la demande est un objet-message porté par le
 * chat », donc le fil change.
 */
export function useDemanderUnCours() {
  const cache = useQueryClient();
  return useMutation({
    mutationFn: (demande: {
      tuteurId: string;
      conversationId: string;
      sujet: string;
      blocage?: string;
    }) =>
      deballer(
        creerUneDemandeDeCours({
          body: {
            tuteurId: demande.tuteurId,
            conversationId: demande.conversationId,
            sujet: demande.sujet,
            ...(demande.blocage === undefined || demande.blocage === ''
              ? {}
              : { blocage: demande.blocage }),
          },
        }),
      ),
    onSuccess: () => {
      void cache.invalidateQueries({ queryKey: CLES.arrivee });
      void cache.invalidateQueries({ queryKey: CLES.conversations.tout });
      void cache.invalidateQueries({ queryKey: CLES.annuaire });
    },
  });
}

/**
 * **ANNULER SA DEMANDE — F-14, ET SANS CE GESTE L'ÉLÈVE EST ENFERMÉ.**
 *
 * « **Une seule demande en attente à la fois**, portée par l'élève, **tous fils
 * confondus**. Pour en adresser une autre, **il annule celle qui attend**. » Et : « aucune
 * modification après envoi : le geste existe déjà, **l'élève annule et renvoie autant de
 * fois qu'il le veut** ».
 *
 * **C'est donc le geste qui rend le plafond d'une demande vivable.** Sans lui, un élève qui
 * s'est trompé de tuteur, ou dont le sujet a changé, attend quinze minutes l'expiration —
 * et l'écran lui montre un bouton grisé sans issue. **Même forme de mur que le plafond de
 * deux cours du tuteur**, et le corpus le ferme de la même façon : par un geste, sur l'écran
 * qui montre le blocage.
 */
export function useAnnulerMaDemande() {
  const cache = useQueryClient();
  return useMutation({
    mutationFn: (demandeId: string) =>
      deballer(annulerUneDemandeDeCours({ path: { id: demandeId } })),
    onSuccess: () => {
      void cache.invalidateQueries({ queryKey: CLES.arrivee });
      void cache.invalidateQueries({ queryKey: CLES.conversations.tout });
      void cache.invalidateQueries({ queryKey: CLES.annuaire });
    },
  });
}

/**
 * ═══════════════════════════════════════════════════════════════════════════
 * **ACCEPTER OU REFUSER UNE DEMANDE — F-49, DEPUIS CET ÉCRAN COMME DEPUIS LE FIL.**
 * ═══════════════════════════════════════════════════════════════════════════
 *
 * « Une demande reçue s'accepte ou se refuse depuis cet écran comme depuis le fil, **sans
 * motif**. » Et F-14 : « le refus est **sec, sans motif** : aucun champ de motif n'existe » —
 * ce hook n'en porte donc aucun, et il n'y a nulle part où en passer un.
 *
 * **L'ACCEPTATION A DEUX FORMES, ET C'EST UNE RÈGLE ET NON UNE COMMODITÉ.** Q298 : « **le
 * même chemin sert à proposer un cours neuf et à demander à rejoindre un cours déjà
 * commencé** ». Le contrat le rend par `coursId` : présent, l'élève rejoint ce cours ;
 * absent, un cours neuf naît.
 *
 * **ET LE TITRE NE SE PRÉREMPLIT PAS DEPUIS LA DEMANDE — C'EST LE CRITÈRE 6 DE F-14.** « Le
 * système DOIT créer le cours **avec le titre et la description que le tuteur saisit**, et
 * **ne recopier ni l'un ni l'autre des deux champs de la demande**. » F-14 en donne le motif :
 * « ces deux champs appartiennent à la demande, et à rien d'autre. Ils **ne passent pas au
 * cours** à l'acceptation, et ne sont recopiés nulle part : le cours garde son propre titre
 * et sa propre description. **Un tuteur qui accepte lit la demande et ouvre son cours ; il
 * ne l'hérite pas.** »
 *
 * **La tentation est forte, et c'est pour cela que le critère existe** : le sujet de l'élève
 * ferait un titre commode. Il ferait aussi entrer, dans le titre d'un cours — lisible par un
 * `orgadmin` et par le compte qui voit tout —, un texte que §5 classe **au niveau le plus
 * haut** et que F-49 garde « fermé à la structure et au compte qui voit tout ».
 */
export function useRepondreAUneDemande() {
  const cache = useQueryClient();
  return useMutation({
    mutationFn: (reponse: {
      demandeId: string;
      accepte: boolean;
      coursId?: string;
      titre?: string;
      description?: string;
    }) =>
      deballer(
        repondreAUneDemandeDeCours({
          path: { id: reponse.demandeId },
          body: {
            reponse: reponse.accepte ? 'acceptee' : 'refusee',
            ...(reponse.coursId === undefined
              ? {}
              : { coursId: reponse.coursId }),
            ...(reponse.titre === undefined ? {} : { titre: reponse.titre }),
            ...(reponse.description === undefined
              ? {}
              : { description: reponse.description }),
          },
        }),
      ),
    onSuccess: () => {
      void cache.invalidateQueries({ queryKey: CLES.arrivee });
      void cache.invalidateQueries({ queryKey: CLES.conversations.tout });
      void cache.invalidateQueries({ queryKey: CLES.annuaire });
    },
  });
}
